import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { SearchIcon } from './SearchIcon';

export interface NoJobsScreenProps {
  /**
   * Whether the backend answered at all.
   *
   * `true` — the pool resolved to nothing and the candidate is **already** in the recruiter queue with the
   * reason *no matching orders* [decision 0095], so the screen may say a person is coming.
   * `false` — the request failed, nobody was enqueued, and the screen must NOT promise one. Same words as
   * KID-2's neutral hand-off, because it is the same situation.
   *
   * Defaults to `true` so `App`'s screen map — and the design-fidelity gate's seed — render the DESIGNED
   * state. Only `JobsScreen` passes `false`, and only when its own request failed.
   */
  readonly enqueued?: boolean;
}

/**
 * Nothing to offer [KJM-1; Figma `07 · No jobs` — 12021:40729 desktop · 40709 iPad · 40690 mobile, all
 * three read 2026-08-21].
 *
 * > ### ⚠️ The frame offers two choices. The model has one, and it already happened.
 * >
 * > The frames draw two option cards: a capture — *"Keep me active for upcoming roles"* with a *"Keep me on
 * > the list"* button — and *"Prefer to talk to someone now?"* with *"Talk to a recruiter"*.
 * >
 * > **Decision 0087 retired the capture.** An Evergreen is now an ordinary order type sitting in the venue's
 * > pool, ranked by the normal keys — it is not a fallback, has no reserved slot, and there is nothing to opt
 * > into. **Decision 0095 makes the recruiter queue the single first-line hand-off**, and the BACKEND
 * > performs it before this screen ever renders, so the second card's button would ask the candidate to
 * > request something that is already done.
 * >
 * > So there is one block, and it **reports** rather than offers. The convention skill § 8 draws exactly this
 * > line: Figma decides how a screen LOOKS, the Ground Truth decides what it SAYS and DOES, and a frame that
 * > has not caught up with an approved decision is a design update owed. The visual treatment below is the
 * > frame's — the 56px feature icon, the centred intro, the option card, the footer button. The words and the
 * > count are 0087's and 0095's.
 *
 * The heading is centred here and left-aligned on the list screen; that is the frame's own difference, not a
 * carry-over.
 */
export function NoJobsScreen({ enqueued = true }: NoJobsScreenProps) {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);
  const back = useBack();

  return (
    <KioskScreen>
      <KioskCard
        band={<Stepper current={JOURNEY_STEP.findJob} />}
        footer={
          <Button fullWidth onClick={endCandidateSession} variant="secondary">
            {t('jobs.empty.done')}
          </Button>
        }
        header={
          <>
            {/*
              Back as well as Done, because the frame draws the shared 48px Header and the badge alone is 33.
              Done is the screen's own closing action and ends the session. Back is `useBack`, the same as
              the list this screen stands in for: a candidate who came from the arrival menu goes back to
              the menu, and anyone else is leaving the visit, so they are asked the Exit question first
              [ISS-149].
            */}
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          <div className="flex w-full flex-col items-center gap-3">
            <SearchIcon />
            <h1
              className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t(enqueued ? 'jobs.empty.heading' : 'jobs.empty.unreachable.title')}
            </h1>
            <p className="w-full text-center text-sm leading-6 text-[var(--muted-foreground)]">
              {t(enqueued ? 'jobs.empty.body' : 'jobs.empty.unreachable.body')}
            </p>
          </div>

          {/*
            The frame's option-card treatment, used for a statement rather than a choice: 1px hairline,
            radius 8, padding 16, 8px between the two lines. It carries no button — the queue entry was
            written server-side before this rendered, and a button here would ask for it twice.
          */}
          {enqueued && (
            <div className="flex w-full flex-col items-start gap-2 rounded-[var(--radius-sm)] border border-solid border-[var(--divider-border)] bg-[var(--card)] p-4">
              <p className="w-full text-base font-semibold leading-6 text-[var(--foreground)]">
                {t('jobs.empty.recruiter.title')}
              </p>
              <p className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
                {t('jobs.empty.recruiter.body')}
              </p>
            </div>
          )}
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
